// client/src/utils/parsingDiscogsHelpers.js

export const safeGet = (arr, index = 0) =>
  Array.isArray(arr) && arr.length > index ? arr[index] : "";

export const cleanName = (name) => name?.replace(/\s\(\d+\)$/, "").trim();

export const getArtists = (releaseData) =>
  releaseData.artists?.map((a) => cleanName(a.name)) || [];

export const normalizeReleaseTitle = (title = "") =>
  title
    .replace(
      /\(Original (Motion Picture )?(Sound ?Track|Score)\)/gi,
      "Soundtrack"
    )
    .replace(/\(Original Soundtrack\)/gi, "Soundtrack")
    .trim();

export const getLabelInfo = (releaseData) => {
  const labels = Array.isArray(releaseData.labels) ? releaseData.labels : [];
  const companies = Array.isArray(releaseData.companies)
    ? releaseData.companies
    : [];

  // Priority: Analogue Productions
  const hasAP =
    labels.some((l) =>
      l.name?.toLowerCase().includes("analogue productions")
    ) ||
    companies.some((c) =>
      c.name?.toLowerCase().includes("analogue productions")
    );

  // Priority: Vinyl Me, Please
  const hasVMP = companies.some((c) =>
    c.name?.toLowerCase().includes("vinyl me, please")
  );

  let label = "";

  if (hasAP) {
    label = "Analogue Productions";
  } else if (hasVMP) {
    label = "Vinyl Me Please";
  } else {
    label = cleanName(labels[0]?.name || "");
  }

  const catalogNumber = labels.map((l) => l.catno).filter(Boolean);

  return {
    label,
    catalogNumber,
  };
};


export const getReleaseYear = (releaseData) => {
  const year = releaseData.released?.substring(0, 4) || releaseData.year?.toString() || "";
  // Treat "0" or non-numeric values as no year
  return year && year !== "0" && /^\d{4}$/.test(year) ? year : "";
};

export const getGenreStyle = (releaseData) => {
  const genre = Array.isArray(releaseData.genres) ? releaseData.genres : [];
  let style = Array.isArray(releaseData.styles) ? releaseData.styles : [];

  // Fallback: if no styles, use genres
  if (style.length === 0) {
    style = genre;
  }

  return { genre, style };
};

export const getFormat = (releaseData) => {
  const rawFormat = safeGet(releaseData.formats)?.name || "";
  return rawFormat === "Vinyl" ? "Record" : rawFormat;
};

export const getColor = (releaseData) => {
  const COLOR_KEYWORDS = [
    "red",
    "blue",
    "green",
    "yellow",
    "swirl",
    "clear",
    "splatter",
    "marble",
    "black",
    "white",
    "pink",
    "orange",
    "purple",
    "silver",
    "gold",
    "gray",
    "grey",
    "brown",
    "smoke",
    "multicolor",
  ];

  const NON_COLOR_TERMS = [
    "uncensored sleeve",
    "censored sleeve",
    "promo sleeve",
  ];

  const colorTexts = (releaseData.formats || [])
    .map((f) => f.text)
    .filter(Boolean)
    .map((text) => text.split(",")[0].trim())
    .filter(
      (text) =>
        COLOR_KEYWORDS.some((keyword) =>
          text.toLowerCase().includes(keyword)
        ) && !NON_COLOR_TERMS.includes(text.toLowerCase())
    );

  const colorString = colorTexts.length > 0 ? colorTexts.join(", ") : "Black";
  return colorString.slice(0, 65);
};

export const getAllDescriptions = (releaseData) =>
  (releaseData.formats || []).flatMap((f) => f.descriptions || []);

export const getFeatures = (releaseData, color) => {
  const FEATURE_KEYWORDS = [
    "Gatefold",
    "Insert",
    "Poster",
    "Sticker",
    "Shrink",
    "Obi",
    "Booklet",
    "Box Set",
    "Numbered",
    "Hype Sticker",
    "Etched",
    "Download Card",
    "Deluxe",
    "Die-Cut",
    "Picture Disc",
    "Clarity Vinyl",
    "Textured Sleeve"
  ];

  const features = [];

  // Collect all descriptions and text fields
  const allDescriptions = (releaseData.formats || []).flatMap(
    (f) => f.descriptions || []
  );
  const allTextFields = (releaseData.formats || [])
    .map((f) => f.text)
    .filter(Boolean);

  // Add matching feature keywords from descriptions
  for (const desc of allDescriptions) {
    for (const keyword of FEATURE_KEYWORDS) {
      if (desc.toLowerCase().includes(keyword.toLowerCase())) {
        features.push(keyword);
      }
    }
  }

  // ✅ Add matching feature keywords from text fields too
  for (const text of allTextFields) {
    for (const keyword of FEATURE_KEYWORDS) {
      if (text.toLowerCase().includes(keyword.toLowerCase())) {
        features.push(keyword);
      }
    }
  }

  // ✅ Check for weight indicators like "180g", "200 gram"
  const weightIndicators = [...allDescriptions, ...allTextFields]
    .join(" ")
    .toLowerCase();
  if (
    weightIndicators.includes("180g") ||
    weightIndicators.includes("180 gram") ||
    weightIndicators.includes("200g") ||
    weightIndicators.includes("200 gram")
  ) {
    features.push("180-220 gram");
  }

  // ✅ Add Colored Vinyl if applicable
  if (color && color.toLowerCase() !== "black") {
    features.push("Colored Vinyl");
  }

  // ✅ Remove duplicates and return
  return [...new Set(features)];
};



export const getEdition = (releaseData) => {
  const EDITION_KEYWORDS = [
    "Limited Edition",
    "Subscriber Exclusive",
    "Remastered",
    "Reissue",
    "First Pressing",
    "Promo",
    "Test Pressing",
    "Mono",
    "Stereo",
    "Club Edition",
    "Deluxe Edition",
    "Expanded Edition",
    "Special Edition",
    "Uncensored Sleeve",
    "Censored Sleeve",
  ];

  const allParts = (releaseData.formats || []).flatMap((f) => {
    const parts = [];

    if (f.text) {
      parts.push(...f.text.split(",").map((s) => s.trim()));
    }

    if (Array.isArray(f.descriptions)) {
      parts.push(...f.descriptions);
    }

    return parts;
  });

  const matched = allParts.filter((desc) =>
    EDITION_KEYWORDS.some((keyword) =>
      desc.toLowerCase().includes(keyword.toLowerCase())
    )
  );

  return [...new Set(matched)]; // ✅ remove duplicates
};




export const getSpeed = (releaseData) => {
  const SPEED_PATTERNS = [
    { match: /33(\u2153)?/, value: "33 RPM" },
    { match: /45/, value: "45 RPM" },
    { match: /78/, value: "78 RPM" },
  ];

  const formats = releaseData.formats || [];

  const twelveInchFormats = formats.filter((f) => {
    const descs = f.descriptions || [];

    return (
      descs.some((d) => /\b12\b/.test(d)) || // match "12"
      descs.some((d) => /album/i.test(d)) || // or description includes "Album"
      parseInt(f.qty) >= 2 // or qty implies 2+ discs
    );
  });

  const otherFormats = formats.filter((f) => !twelveInchFormats.includes(f));

  // Helper to extract speed from a list of formats
  const extractSpeed = (formatsArray) => {
    for (const format of formatsArray) {
      const sources = [
        ...(format.descriptions || []),
        ...(format.text ? [format.text] : []),
      ];
      for (const pattern of SPEED_PATTERNS) {
        if (sources.some((s) => pattern.match.test(s))) {
          return pattern.value;
        }
      }
    }
    return null;
  };

  // ✅ Try to get speed from 12" formats first
  const speedFrom12 = extractSpeed(twelveInchFormats);
  if (speedFrom12) return speedFrom12;

  // ✅ If 12" exists but has no speed, assume 33 RPM
  if (twelveInchFormats.length > 0) return "33 RPM";

  // ✅ Otherwise fallback to any other formats
  const speedFromOthers = extractSpeed(otherFormats);
  if (speedFromOthers) return speedFromOthers;

  // Final fallback
  return "33 RPM";
};



export const getRecordSize = (releaseData) => {
  const sizePriority = [12, 10, 7]; // Prioritize larger sizes
  const sizesFound = new Set();

  for (const format of releaseData.formats || []) {
    if (format.name.toLowerCase() !== "vinyl") continue;

    for (const desc of format.descriptions || []) {
      for (const size of sizePriority) {
        if (new RegExp(`\\b${size}\\b`).test(desc)) {
          sizesFound.add(size);
        }
      }
    }
  }

  for (const size of sizePriority) {
    if (sizesFound.has(size)) return `${size}"`;
  }

  return '12"'; // Default fallback
};

export const getUPC = (releaseData) => {
  const barcodes = (releaseData.identifiers || []).filter(
    (id) => id.type.toLowerCase() === "barcode"
  );

  if (barcodes.length === 0) return "";

  // Priority 1: Prefer barcodes marked as "Scanned"
  const scannedBarcode = barcodes.find(
    (bc) => bc.description && bc.description.toLowerCase().includes("scanned")
  );
  
  if (scannedBarcode) {
    // Strip all non-digits (spaces, dashes, etc.)
    return scannedBarcode.value.replace(/[^\d]/g, "");
  }

  // Priority 2: Prefer EAN-13 format (usually most reliable)
  const ean13Barcode = barcodes.find(
    (bc) => bc.description && bc.description.toLowerCase().includes("ean-13")
  );
  
  if (ean13Barcode) {
    return ean13Barcode.value.replace(/[^\d]/g, "");
  }

  // Priority 3: Prefer barcodes without "Text" description (often more reliable)
  const nonTextBarcode = barcodes.find(
    (bc) => !bc.description || !bc.description.toLowerCase().includes("text")
  );
  
  if (nonTextBarcode) {
    return nonTextBarcode.value.replace(/[^\d]/g, "");
  }

  // Fallback: Use first barcode found
  const raw = barcodes[0]?.value || "";
  return raw.replace(/[^\d]/g, "");
};

export const getTotalVinylQty = (releaseData) => {
  const vinylFormats = (releaseData.formats || []).filter(
    (f) => f.name.toLowerCase() === "vinyl"
  );

  return vinylFormats.reduce((sum, f) => {
    const q = parseInt(f.qty || "1");
    return sum + (isNaN(q) ? 0 : q);
  }, 0);
};

export const getType = (releaseData, totalQty) => {
  const vinylFormats = (releaseData.formats || []).filter(
    (f) => f.name?.toLowerCase() === "vinyl"
  );

  const allDescriptions = vinylFormats.flatMap((f) => f.descriptions || []);

  const hasBoxSet = allDescriptions.some((d) =>
    d.toLowerCase().includes("box set")
  );

  const isLP = allDescriptions.some((d) =>
    ["lp", "album"].includes(d.toLowerCase())
  );

  if (hasBoxSet || totalQty >= 4) return "Box Set";

  if (isLP) {
    if (totalQty === 3) return "Triple LP";
    if (totalQty === 2) return "Double LP";
    return "LP";
  }

  if (allDescriptions.some((d) => d.toLowerCase() === "ep")) return "EP";
  if (allDescriptions.some((d) => d.toLowerCase() === "single"))
    return "Single";

  return "";
};

export const getNotesForDescription = (releaseData) => {
  const notes = releaseData.notes;
  if (!notes) return [];

  // Split notes by lines and filter for bullet points or lines starting with "-"
  const lines = notes.split('\n').map(line => line.trim()).filter(Boolean);

  const bulletPoints = lines.filter(line =>
    line.startsWith('-') ||
    line.startsWith('•') ||
    line.match(/^\d+\.\s/) // numbered lists like "1. "
  );

  // Clean up bullet points - remove leading dashes/bullets and trim
  return bulletPoints.map(point =>
    point.replace(/^[-•]\s*/, '').replace(/^\d+\.\s*/, '').trim()
  ).filter(point => point.length > 0);
};

export const getKeySellingPoints = (releaseData) => {
  const notes = releaseData.notes;
  if (!notes) return [];

  // Convert to lowercase for matching but preserve original case for output
  const notesLower = notes.toLowerCase();
  const sellingPoints = [];

  // Split notes into sentences for full sentence extraction
  const notesSentences = notes.split(/[.!?]+/).map(s => s.trim()).filter(Boolean);

  // Limited Edition patterns
  const limitedEditionMatch = notes.match(/limited edition of (\d+[\,\d]*)/i) ||
                             notes.match(/limited to (\d+[\,\d]*) copies/i) ||
                             notes.match(/(\d+[\,\d]*) copies? (only|limited)/i) ||
                             notes.match(/(\d+[\,\d]*) foil-stamped/i) ||
                             notes.match(/(\d+[\,\d]*) numbered copies/i);
  if (limitedEditionMatch) {
    const number = limitedEditionMatch[1].replace(/,/g, '');
    sellingPoints.push(`Limited Edition of ${number}`);
  }

  // Special vinyl colors/variants (but skip basic "black" and weight info)
  const colorMatch = notes.match(/pressed on ([^.]+vinyl)/i);
  if (colorMatch && !colorMatch[1].toLowerCase().includes('black vinyl')) {
    let colorText = colorMatch[1].trim();
    // Clean up common redundant words and weight info
    colorText = colorText
      .replace(/\bvinyl\b/i, '')
      .replace(/\b\d{2,3}\s*g(rams?)?\b/gi, '')  // "180g", "180 Grams", "200 gram"
      .replace(/\b(of\s+)?high\s+definition\b/gi, '')  // MoFi "High Definition Vinyl"
      .replace(/\bexclusive\b/gi, '')
      .replace(/\s{2,}/g, ' ')
      .trim();
    if (colorText && colorText.length > 1) {
      sellingPoints.push(`${colorText} Vinyl`);
    }
  }

  // Extract full sentences about exclusive releases
  for (const sentence of notesSentences) {
    const sentenceLower = sentence.toLowerCase();
    if (sentenceLower.includes('exclusive') ||
        (sentenceLower.includes('record store day') || sentenceLower.includes('rsd'))) {
      let cleanSentence = sentence.replace(/^\s*[-•]\s*/, '').trim();
      // Remove download card references even from full sentences
      cleanSentence = cleanSentence.replace(/\s*&\s*a download card[^.]*/, '').replace(/download card[^,.]*[,.]?/gi, '').trim();
      if (cleanSentence && cleanSentence.length > 5) {
        sellingPoints.push(cleanSentence);
      }
    }
  }

  // Special packaging (Gatefold excluded — not important for descriptions)
  if (notesLower.includes('obi') && notesLower.includes('strip')) {
    sellingPoints.push('OBI Strip');
  }

  // Remastered versions
  if (notesLower.includes('remastered from original') || notesLower.includes('remastered')) {
    if (notesLower.includes('original tapes') || notesLower.includes('master tapes')) {
      sellingPoints.push('Remastered from Original Tapes');
    } else {
      sellingPoints.push('Remastered');
    }
  }

  // Numbered copies
  if (notesLower.includes('hand numbered') || notesLower.includes('individually numbered') || notesLower.includes('numbered copies')) {
    sellingPoints.push('Numbered Edition');
  }

  // Foil stamped
  if (notesLower.includes('foil-stamped') || notesLower.includes('foil stamped')) {
    sellingPoints.push('Foil Stamped');
  }

  // Extract full sentences about special inserts/extras instead of generic text
  for (const sentence of notesSentences) {
    const sentenceLower = sentence.toLowerCase();
    // Look for sentences about posters, booklets, or other extras
    if ((sentenceLower.includes('poster') && (sentenceLower.includes('comes with') || sentenceLower.includes('includes'))) ||
        (sentenceLower.includes('booklet') && (sentenceLower.includes('comes with') || sentenceLower.includes('includes'))) ||
        (sentenceLower.includes('insert') && (sentenceLower.includes('comes with') || sentenceLower.includes('includes')))) {
      // Clean up and add the full sentence, removing download card references
      let cleanSentence = sentence.replace(/^\s*[-•]\s*/, '').trim();
      cleanSentence = cleanSentence.replace(/\s*&\s*a download card[^.]*/, '').replace(/download card[^,.]*[,.]?/gi, '').trim();
      if (cleanSentence && cleanSentence.length > 10) {
        sellingPoints.push(cleanSentence);
      }
    }
  }

  // Remove duplicates - if we have full descriptive sentences, skip generic ones
  const finalSellingPoints = [];
  const addedConcepts = new Set();

  for (const point of sellingPoints) {
    const pointLower = point.toLowerCase();

    // Skip generic versions if we have descriptive ones
    if (pointLower === 'obi strip' && sellingPoints.some(p => p.toLowerCase().includes('obi') && p.length > 20)) {
      continue;
    }

    finalSellingPoints.push(point);
  }

  // Download cards - REMOVED: They get lost easily and nobody uses them anymore

  return finalSellingPoints;
};
